import { Icon } from "icons";
import { useTranslation } from "react-i18next";
import { Button, cn } from "ui";
import { formatBillingAmountRounded } from "utils";

import { getBillingProductFeatureLabelKey } from "src/helpers/billingConfig";
import {
  getAthleteCountLabel,
  getAthleteLimitLabel,
  getPriceUnitLabel,
} from "src/helpers/billingLabels";
import { type BillingProduct } from "src/queries/billing";

import { BillingFeatureItem } from "./BillingFeatureItem";

interface Props {
  product: BillingProduct;
  interval: "MONTH" | "YEAR";
  onSelect: (product: BillingProduct) => void;
  ctaLabel?: string;
  ctaLoading?: boolean;
  ctaDisabled?: boolean;
  ctaClassName?: string;
  trialNote?: string;
}

export function PricingProductCard(props: Props) {
  const { t } = useTranslation();
  const price = props.product.prices.find(
    (candidate) => candidate.interval === props.interval,
  );

  if (!price) {
    return null;
  }

  return (
    <div
      className={cn("relative flex flex-col rounded-2xl border bg-white p-6", {
        "border-szt-black shadow-[10px_10px_0_var(--szt-pink)]":
          props.product.featured,
        "border-gray-200": !props.product.featured,
      })}
    >
      {props.product.featured && (
        <span className="absolute right-4 top-4 rounded-full bg-szt-black px-3 py-1 text-xs font-medium text-white">
          {t("pricing.mostPopular")}
        </span>
      )}
      <h3 className="text-2xl font-semibold text-gray-900">
        {props.product.name}
      </h3>
      <p className="mt-1 text-sm text-gray-500">
        {getAthleteLimitLabel(t, props.product.athleteLimit)}
      </p>
      <p className="mt-6 text-4xl font-bold tracking-tight text-gray-900">
        {formatBillingAmountRounded(price.unitAmount, price.currency)}
      </p>
      <p className="mt-1 text-sm text-gray-500">
        {getPriceUnitLabel(t, props.interval)}
      </p>
      <p className="mt-2 text-sm text-gray-700">{t("pricing.includesVat")}</p>
      <div className="my-6 h-px bg-gray-200" />
      <ul className="flex-1 space-y-3">
        <BillingFeatureItem>
          {getAthleteCountLabel(t, props.product.athleteLimit)}
        </BillingFeatureItem>
        {props.product.features.map((feature) => {
          const labelKey = getBillingProductFeatureLabelKey(feature.key);
          return (
            <BillingFeatureItem
              key={feature.key}
              comingSoon={feature.comingSoon}
            >
              {t(labelKey)}
            </BillingFeatureItem>
          );
        })}
      </ul>
      {props.trialNote && (
        <p className="mt-8 text-center text-xs text-gray-500">
          {props.trialNote}
        </p>
      )}
      <Button
        variant="default"
        className={cn(
          "h-12 w-full rounded-[2px] transition-[border-radius,color,background-color] duration-100 ease-out hover:rounded-[24px]",
          props.trialNote ? "mt-2" : "mt-8",
          "bg-szt-black text-white hover:bg-szt-black/90",
          props.ctaClassName,
        )}
        loading={props.ctaLoading}
        disabled={props.ctaDisabled || props.ctaLoading}
        onClick={() => {
          props.onSelect(props.product);
        }}
      >
        {props.ctaLabel ?? t("pricing.startFreeTrial")}
        <Icon name="tabler-arrow-right" className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}
